import { nanoid } from "nanoid"
import { withTypesenseKeyRetry } from "@/utils/typesenseRetry"
import Logger from "@/utils/Logger"
import { typesenseApiKeyCacheKey } from "@/utils/typesenseApiKeyCacheKey"

export async function useTypesenseJobCounts() {
  const { $api } = useNuxtApp()
  const { t, locales, loadLocaleMessages } = useI18n()
  const { tenantId, jobShopId } = useHeaderStore()
  const { data: typesenseApiKey } = useNuxtData(typesenseApiKeyCacheKey(jobShopId))
  const { public: { typesenseUrl } } = useRuntimeConfig()
  const { data: jobShop } = useNuxtData("jobShopData")
  // The jobshop locale arrives as a region tag ("de-DE"); resolve it to a registered
  // i18n code ("de") so t() resolves deterministically (see resolveI18nLocale).
  const jobShopLocale = computed(() => resolveI18nLocale(jobShop.value?.locale, locales.value.map(l => l.code), "en"))
  const { refreshTypesenseApiKey } = useTypesenseKeyRefresh()

  const requestId = nanoid()
  const contextString = `TypeSense counters for tenantId: "${tenantId}", jobShopId: "${jobShopId}"`

  let startTime
  if (import.meta.server) {
    Logger(requestId).start(`Start fetching ${contextString}`)
    startTime = Date.now()
  }

  if (!typesenseApiKey.value) {
    if (import.meta.server)
      Logger(requestId).error(`Skipping ${contextString}: Typesense API key is missing`)

    return {
      jobsCountAll: computed(() => ""),
      jobsCountCategories: computed(() => []),
      typesenseJobCountsStatus: ref("error"),
      typesenseJobCountsError: ref(new Error("Typesense API key is missing")),
      jobCountsUnavailable: computed(() => true),
    }
  }

  const { data, status: typesenseJobCountsStatus, error: typesenseJobCountsError } = await useAsyncData("typesenseJobCounts", () => withTypesenseKeyRetry(
    key => $api.typesense.getJobCount(tenantId, jobShopId, key, typesenseUrl),
    typesenseApiKey.value,
    refreshTypesenseApiKey,
  ), {
    // Only extract the raw counts here. Translation must NOT happen in this transform:
    // it runs during SSR setup, before the active locale is switched to the jobshop
    // locale and before that locale's messages are loaded (te("...","de") is still
    // false at this point), so t() would resolve to English. The job-count strings are
    // instead computed at render time below, where the locale is active and loaded.
    transform: (rawData) => {
      return {
        count: rawData?.results?.[0]?.found ?? 0,
        categories: (rawData?.results?.[0]?.facet_counts?.[0]?.counts ?? []) as any[],
      }
    },
  })

  // The job-count strings are translated into the jobshop locale (e.g. "de"), which is
  // not necessarily the active/browser-detected locale, so its messages may not be loaded
  // yet. Load them explicitly so the render-time computeds below resolve correctly
  // regardless of the visitor's Accept-Language (otherwise vue-i18n falls back to English).
  await loadLocaleMessages(jobShopLocale.value)

  if (import.meta.server) {
    if (!typesenseJobCountsError.value) {
      const duration = Date.now() - startTime
      Logger(requestId).success(`Successfully fetched ${contextString}. (${duration}ms)`)
    }
    else {
      const errorMessage = typesenseJobCountsError.value?.data?.errors?.[0]?.message || typesenseJobCountsError.value?.data?.description || "Unknown error occurred"
      Logger(requestId).error(`Error while fetching ${contextString}: ${errorMessage}\n\n`, typesenseJobCountsError.value)
    }
  }

  return {
    jobsCountAll: computed(() => data.value ? t("module.jobs-text", data.value.count, { locale: jobShopLocale.value }) : ""),
    jobsCountCategories: computed(() => (data.value?.categories ?? []).map(jobCount => ({
      ...jobCount,
      jobsText: t("module.jobs-text", jobCount.count, { locale: jobShopLocale.value }),
    }))),
    typesenseJobCountsStatus,
    typesenseJobCountsError,
    jobCountsUnavailable: computed(() => !!typesenseJobCountsError.value),
  }
}
